/**
 * Model for document field review (HITL) state.
 *
 * A dataset can hold many parked documents, so this model tracks a LIST of
 * documents and stages the reviewer's decisions per document. The release unit
 * is the document: each document is committed on its own as the reviewer
 * advances, so partial progress across a dataset is the intended outcome, not a
 * failure to recover from.
 *
 * Resolutions are staged locally within a document (so the reviewer can revise
 * freely before committing) and flushed in one call per document.
 *
 * IDENTITY: a flag is keyed by `entity_id` (UUID), never by `field_id`. Entity
 * type is NOT unique within a document — one real scanned form produced
 * `table_1` three times, and 163 documents in the corpus have repeated types.
 * Keying on field_id would collapse siblings in the UI and leave the extras
 * unresolvable, parking the document forever. `field_id` is a display label
 * only and duplicates are expected.
 */
export class DocumentReviewModel {
  constructor() {
    this.datasetId = null;
    this.datasetName = '';
    this.floor = null;
    this.phase = 'idle'; // idle | loading | reviewing | submitting | completed | failed
    this.documents = []; // [{ document_id, file_id, file_name, flags: [...] }]
    this.activeIndex = 0;
    // document_id -> { entity_id -> { action, corrected_value } } staged, uncommitted
    this.staged = {};
    // document_id -> 'committed' — set once the server accepted that document
    this.committed = {};
    // Flags on documents released this session. They leave `documents` on
    // commit, so this is what keeps them in the footer's running total.
    this.committedFieldCount = 0;
    // entity_ids the server named in a 422 incomplete_review, for the active doc
    this.invalidFields = [];
    this.error = null;
    // Opaque cursor for the next page of parked documents; null = last page.
    this.nextCursor = null;
    // True while a cursor page is in flight, so the Load more control can be
    // disabled instead of fanning out duplicate requests on repeated clicks.
    this.loadingMore = false;
    this.listeners = [];
  }

  // No sessionStorage "already submitted" latch here, unlike the CSV flow.
  // CSV needs one because its dataset stays in `awaiting_input` until the
  // pipeline resumes, so the Review button would otherwise re-offer a job that
  // was already approved. Document review has no equivalent window: a committed
  // document is removed from this list immediately, and the card's button is
  // driven by the server's own `file_counts.awaiting_review`. A latch here would
  // be write-only state that reads like protection without providing any.

  setDatasetId(datasetId) {
    this.datasetId = datasetId;
    this.notify();
  }

  setDatasetName(name) {
    this.datasetName = name || '';
    this.notify();
  }

  setPhase(phase) {
    this.phase = phase;
    this.notify();
  }

  setError(error) {
    this.error = error;
    this.notify();
  }

  /**
   * Load a pending-review payload.
   *
   * A parked document may legitimately have no flags: a scan too degraded for
   * OCR to read parks on a document-level tripwire, with no field ever measured.
   * Keeping it is the point — dropping it hides a parked document from its own
   * reviewer and leaves the panel claiming everything is clear.
   */
  setReviewData(payload, { preserveCommitted = false } = {}) {
    this.floor = payload?.floor ?? null;
    this.documents = payload?.documents || [];
    this.nextCursor = payload?.next_cursor ?? null;
    this.activeIndex = 0;
    this.staged = {};
    // A stale-cursor reset reloads the list but must not forget documents that
    // really were released earlier in the session — that tally drives the
    // completion copy.
    if (!preserveCommitted) {
      this.committed = {};
      this.committedFieldCount = 0;
    }
    this.invalidFields = [];
    this.notify();
  }

  /** Append the next cursor page, preserving staged work on already-loaded docs. */
  appendReviewData(payload) {
    const more = payload?.documents || [];
    const seen = new Set(this.documents.map(d => d.document_id));
    this.documents = [...this.documents, ...more.filter(d => !seen.has(d.document_id))];
    this.nextCursor = payload?.next_cursor ?? null;
    this.notify();
  }

  setLoadingMore(loading) {
    this.loadingMore = !!loading;
    this.notify();
  }

  setActiveIndex(index) {
    if (index >= 0 && index < this.documents.length) {
      this.activeIndex = index;
      this.invalidFields = [];
      this.notify();
    }
  }

  getActiveDocument() {
    return this.documents[this.activeIndex] || null;
  }

  /**
   * Stage one field decision for a document, keyed by entity_id.
   * @param {string} documentId
   * @param {string} entityId the flag's UUID — NOT its field_id
   * @param {'confirm'|'correct'|'skip'} action
   * @param {string} [correctedValue] required when action === 'correct'
   */
  stageResolution(documentId, entityId, action, correctedValue) {
    if (!this.staged[documentId]) this.staged[documentId] = {};
    this.staged[documentId][entityId] =
      action === 'correct'
        ? { action, corrected_value: correctedValue ?? '' }
        : { action };
    // Acting on a field clears its 422 highlight immediately.
    this.invalidFields = this.invalidFields.filter(id => id !== entityId);
    this.notify();
  }

  /**
   * Stage a raw input value, classifying it against what extraction returned:
   * unchanged means the extraction was right (`confirm`), edited means it was
   * not (`correct`). Comparison is trimmed, so whitespace alone is not an edit.
   * @returns {boolean} false if the value was empty or the entity is unknown
   */
  stageValue(documentId, entityId, rawValue) {
    const doc = this.documents.find(d => d.document_id === documentId);
    const flagRow = (doc?.flags || []).find(f => f.entity_id === entityId);
    if (!flagRow) return false;

    const next = String(rawValue ?? '').trim();
    if (!next) return false;

    const extracted = String(flagRow.extracted_value ?? '').trim();
    if (next === extracted) {
      this.stageResolution(documentId, entityId, 'confirm');
    } else {
      this.stageResolution(documentId, entityId, 'correct', next);
    }
    return true;
  }

  unstageResolution(documentId, entityId) {
    if (this.staged[documentId]) {
      delete this.staged[documentId][entityId];
      this.notify();
    }
  }

  getStagedResolution(documentId, entityId) {
    return this.staged[documentId]?.[entityId] || null;
  }

  /**
   * Any decision staged anywhere in this session that has not been committed.
   *
   * Drives the confirm on the way out: every dismissal gesture on the panel
   * ends in `reset()`, which drops `staged` silently, so this is what tells a
   * caller there is something to lose. Deliberately NOT `isDocumentComplete` —
   * a half-finished document is exactly the case worth protecting, and it is
   * the one a completeness check would miss.
   *
   * Committed documents do not count: they are already released server-side,
   * so closing loses nothing.
   */
  hasStagedWork() {
    return Object.values(this.staged).some(byEntity => Object.keys(byEntity || {}).length > 0);
  }

  /** Every flag on this document has a staged decision. */
  isDocumentComplete(documentId) {
    const doc = this.documents.find(d => d.document_id === documentId);
    if (!doc) return false;
    const staged = this.staged[documentId] || {};
    const flags = doc.flags || [];
    return flags.length > 0 && flags.every(f => !!staged[f.entity_id]);
  }

  isDocumentCommitted(documentId) {
    return this.committed[documentId] === 'committed';
  }

  /**
   * Records that the server accepted this document AND released it. Only
   * released documents count — a 2xx that comes back `released: false` left the
   * document parked, so counting it would overstate what the reviewer finished.
   */
  markDocumentCommitted(documentId) {
    // Counted here, before removeDocument drops the flags with the document.
    if (this.committed[documentId] !== 'committed') {
      const doc = this.documents.find(d => d.document_id === documentId);
      this.committedFieldCount += (doc?.flags || []).length;
    }
    this.committed[documentId] = 'committed';
    this.notify();
  }

  /**
   * The resolutions array for one document, in the submit contract's shape.
   * entity_id is the reference the server resolves against; field_id rides along
   * as the human-readable label.
   */
  buildResolutions(documentId) {
    const doc = this.documents.find(d => d.document_id === documentId);
    if (!doc) return [];
    const staged = this.staged[documentId] || {};
    return (doc.flags || [])
      .filter(f => staged[f.entity_id])
      .map(f => ({
        entity_id: f.entity_id,
        field_id: f.field_id,
        ...staged[f.entity_id],
      }));
  }

  /**
   * Replace one document's flags in place — used after a 409, where the server's
   * current flag set is authoritative and ours is stale.
   */
  replaceDocumentFlags(documentId, flags) {
    const doc = this.documents.find(d => d.document_id === documentId);
    if (!doc) return;
    doc.flags = flags || [];
    delete this.staged[documentId];
    this.notify();
  }

  removeDocument(documentId) {
    const idx = this.documents.findIndex(d => d.document_id === documentId);
    if (idx === -1) return;
    this.documents.splice(idx, 1);
    delete this.staged[documentId];
    if (this.activeIndex >= this.documents.length) {
      this.activeIndex = Math.max(0, this.documents.length - 1);
    }
    this.notify();
  }

  /** @param {string[]} entityIds the 422's `missing` list — entity_ids, not labels */
  setInvalidFields(entityIds) {
    this.invalidFields = Array.isArray(entityIds) ? entityIds : [];
    this.notify();
  }

  /** Counts for the footer: staged/committed vs total flags on the active doc. */
  getActiveProgress() {
    const doc = this.getActiveDocument();
    if (!doc) return { settled: 0, total: 0 };
    const staged = this.staged[doc.document_id] || {};
    const flags = doc.flags || [];
    return {
      settled: flags.filter(f => !!staged[f.entity_id]).length,
      total: flags.length,
    };
  }

  /**
   * Counts for the footer across EVERY document in this review, not just the
   * active one — design's call (review feedback 2026-10-06): "x of N fields
   * settled" is the reviewer's progress through the whole queue. Documents
   * released this session still count, as settled, via committedFieldCount.
   */
  getOverallProgress() {
    let settled = this.committedFieldCount;
    let total = this.committedFieldCount;
    for (const doc of this.documents) {
      // Already in the tally above; it only lingers here until removeDocument.
      if (this.committed[doc.document_id] === 'committed') continue;
      const staged = this.staged[doc.document_id] || {};
      const flags = doc.flags || [];
      total += flags.length;
      settled += flags.filter(f => !!staged[f.entity_id]).length;
    }
    return { settled, total };
  }

  getCommittedCount() {
    return Object.keys(this.committed).length;
  }

  reset() {
    this.datasetId = null;
    this.datasetName = '';
    this.floor = null;
    this.phase = 'idle';
    this.documents = [];
    this.activeIndex = 0;
    this.staged = {};
    this.committed = {};
    this.committedFieldCount = 0;
    this.invalidFields = [];
    this.error = null;
    this.nextCursor = null;
    this.loadingMore = false;
    this.notify();
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notify() {
    const state = this.getState();
    this.listeners.forEach(listener => listener(state));
  }

  getState() {
    return {
      datasetId: this.datasetId,
      datasetName: this.datasetName,
      floor: this.floor,
      phase: this.phase,
      documents: this.documents,
      activeIndex: this.activeIndex,
      staged: this.staged,
      committed: this.committed,
      invalidFields: this.invalidFields,
      error: this.error,
      nextCursor: this.nextCursor,
      loadingMore: this.loadingMore,
    };
  }
}

export default DocumentReviewModel;
